import React, { useContext } from "react";
import { AppContext } from "../../App";
import { 
  IoPhonePortraitOutline, 
  IoGlobeOutline, 
  IoTerminalOutline,
  IoCardOutline,
  IoCheckmarkCircleOutline,
  IoArrowForward
} from "react-icons/io5";

import Footer from "../../ones/Footer";

export default function SoftwareServices() {
  const appContext = useContext(AppContext);

  const coreExpertise = [
    {
      id: "webDev",
      title: "Website Development",
      icon: <IoGlobeOutline />,
      scripture: "Deploying high-performance portals and progressive web applications (PWAs). We focus on SEO-optimized, low-latency architecture that converts visitors into customers.",
      features: ["Single Page Applications", "Corporate Portals", "E-commerce Ecosystems"]
    },
    {
      id: "appDev",
      title: "Application Development",
      icon: <IoPhonePortraitOutline />,
      scripture: "Crafting native and hybrid mobile experiences. From iOS Swift to Android Kotlin and cross-platform Flutter, we build apps that stay in the user's dock.",
      features: ["Native iOS & Android", "React Native Solutions", "Real-time Syncing"]
    },
    {
      id: "systemDev",
      title: "Systems Development",
      icon: <IoTerminalOutline />,
      scripture: "Engineering the 'Brain' of your business. We build robust CRM, ERP, and bespoke automation software designed to handle complex business logic.",
      features: ["Bespoke ERP & CRM", "Workflow Automation", "API First Architecture"]
    }
  ];

  return (
    <>
      <style>{`
        .s-software-page { 
          background: #060b10; 
          color: var(--lightColor); 
          padding-bottom: 0; 
          font-family: 'Inter', sans-serif;
          overflow: hidden;
          position: relative;
        }

        .s-bg-glow {
          position: absolute;
          top: 20%;
          right: -10%;
          width: 500px;
          height: 500px;
          background: var(--primaryColor);
          filter: blur(180px);
          opacity: 0.05;
          pointer-events: none;
        }

        .s-container { max-width: 1250px; margin: 0 auto; padding: 0 40px; position: relative; z-index: 2; }
        
        .s-hero { padding: 160px 0 100px; text-align: left; }
        .s-main-title { font-size: clamp(2.8rem, 6vw, 4.5rem); font-weight: 900; line-height: 1.05; margin-bottom: 25px; letter-spacing: -0.05em; }
        .s-hero-desc { font-size: 1.25rem; color: rgba(255,255,255,0.5); max-width: 750px; line-height: 1.7; }

        /* ── Modern Seamless Connection Line ── */
        .s-grid { 
          display: grid; 
          grid-template-columns: repeat(3, 1fr); 
          gap: 30px; 
          margin-bottom: 100px;
          position: relative;
        }

        /* The Animated Connector Line */
        .s-grid::before {
          content: "";
          position: absolute;
          top: 95px; /* Aligned with icon center */
          left: 10%;
          right: 10%;
          height: 1px;
          background: linear-gradient(90deg, 
            transparent 0%, 
            rgba(255,255,255,0.05) 20%, 
            var(--primaryColor) 50%, 
            rgba(255,255,255,0.05) 80%, 
            transparent 100%
          );
          opacity: 0.3;
          z-index: 0;
        }

        /* Decorative Nodes on the line */
        .s-grid::after {
          content: "";
          position: absolute;
          top: 91px;
          left: 10%;
          right: 10%;
          height: 9px;
          background-image: radial-gradient(circle, var(--primaryColor) 1px, transparent 1.5px);
          background-size: 33.33% 100%;
          opacity: 0.4;
          z-index: 0;
        }

        .s-card { 
          background: rgba(17, 29, 41, 0.4);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.05);
          padding: 60px 45px; 
          border-radius: 32px; 
          display: flex;
          flex-direction: column;
          transition: all 0.5s cubic-bezier(0.2, 1, 0.3, 1);
          position: relative;
          overflow: hidden;
          z-index: 1; /* Sits above the line */
        }

        .s-card::before {
          content: "";
          position: absolute;
          top: 0; left: 0; width: 100%; height: 4px;
          background: linear-gradient(90deg, var(--primaryColor), var(--secondaryColor));
          opacity: 0;
          transition: 0.4s;
        }

        .s-card:hover { 
          background: rgba(17, 29, 41, 0.8);
          transform: translateY(-15px); 
          border-color: rgba(255, 255, 255, 0.15);
          box-shadow: 0 30px 60px rgba(0,0,0,0.4);
        }

        .s-card:hover::before { opacity: 1; }

        .s-card-icon { 
          width: 70px;
          height: 70px;
          background: #060b10; /* Solid back to hide line behind icon */
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2.2rem; 
          color: var(--secondaryColor); 
          margin-bottom: 35px;
          transition: 0.4s;
          position: relative;
          z-index: 2;
        }

        .s-card:hover .s-card-icon {
          background: var(--secondaryColor);
          color: #fff;
          transform: rotate(-5deg);
          border-color: var(--secondaryColor);
        }

        .s-card-title { font-size: 1.75rem; font-weight: 800; margin-bottom: 18px; letter-spacing: -0.02em; }
        .s-card-text { color: rgba(255,255,255,0.45); font-size: 1rem; line-height: 1.7; margin-bottom: 30px; }
        
        .s-feat-list { list-style: none; padding: 0; margin-bottom: 40px; flex-grow: 1; }
        .s-feat-item { display: flex; align-items: center; gap: 12px; font-size: 0.9rem; color: rgba(255,255,255,0.7); margin-bottom: 14px; }
        .s-feat-icon { color: var(--primaryColor); font-size: 1.2rem; }

        .s-card-cta {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          text-transform: uppercase;
          font-size: 0.8rem;
          letter-spacing: 2px;
          color: var(--secondaryColor);
          transition: 0.3s;
          padding: 12px 20px;
          width: fit-content;
          border: 1px solid transparent;
        }

        .s-card-cta:hover { 
          background: var(--lightColor); 
          color: #000;
          gap: 18px; 
          opacity: .95; 
          font-weight: 700; 
        }

        .s-payment-strip { 
          background: linear-gradient(145deg, #111d29 0%, #060b10 100%); 
          border-radius: 40px; 
          padding: 80px; 
          display: grid; 
          grid-template-columns: 1fr 1.5fr; 
          gap: 80px; 
          align-items: center; 
          border: 1px solid rgba(255,255,255,0.03);
          margin-bottom: 120px;
        }
        
        .s-pay-icon-box { 
          width: 100px; height: 100px; 
          background: var(--primaryColor); 
          border-radius: 24px; 
          display: flex; align-items: center; justify-content: center; 
          font-size: 3rem; color: #fff; margin-bottom: 25px;
          box-shadow: 0 15px 35px rgba(52, 152, 219, 0.3);
        }
        .s-pay-title { font-size: 2.5rem; font-weight: 900; margin-bottom: 20px; line-height: 1.1; }
        .s-pay-text { color: rgba(255,255,255,0.5); line-height: 1.8; font-size: 1.1rem; }

        @media (max-width: 1100px) {
          .s-grid { grid-template-columns: 1fr 1fr; }
          .s-grid::before, .s-grid::after { display: none; } /* Hide line on wrap for clean look */
        }

        @media (max-width: 768px) {
          .s-grid { grid-template-columns: 1fr; }
          .s-payment-strip { grid-template-columns: 1fr; padding: 40px; gap: 40px; }
          .s-main-title { font-size: 2.5rem; }
          .s-container { padding: 0 25px; }
        }
      `}</style>

      <div className="s-software-page">
        <div className="s-bg-glow" />
        
        <section className="s-hero">
          <div className="s-container">
            <h1 className="s-main-title text-secondary">
              Custom Software Services <br/> 
              <span className="text-light">for Modern Growth.</span>
            </h1>
            <p className="s-hero-desc">
              We translate complex business requirements into scalable, secure, and intuitive digital products. 
              Our approach balances technical rigor with exceptional user experience.
            </p>
          </div>
        </section>

        <section className="s-container">
          <div className="s-grid">
            {coreExpertise.map((item) => (
              <div key={item.id} className="s-card">
                <div className="s-card-icon">{item.icon}</div>
                <h3 className="s-card-title">{item.title}</h3>
                <p className="s-card-text">{item.scripture}</p>
                
                <ul className="s-feat-list">
                  {item.features.map((feat, i) => (
                    <li key={i} className="s-feat-item">
                      <IoCheckmarkCircleOutline className="s-feat-icon" />
                      {feat}
                    </li>
                  ))}
                </ul>

                <div 
                  className="s-card-cta rounded" 
                  onClick={() => appContext.navTo({ item: "services", subItem: "software", extraItem: item.id })}
                  style={{ cursor: "pointer" }}
                >
                  View Service <IoArrowForward />
                </div>
              </div>
            ))}
          </div>

          <div className="s-payment-strip">
            <div>
              <div className="s-pay-icon-box">
                <IoCardOutline />
              </div>
              <h2 className="s-pay-title">FinTech & Payment Gateway Integration</h2>
            </div>
            <div>
              <p className="s-pay-text">
                Monetize your platform with seamless payment engineering. We specialize in secure 
                <strong> SELCOM </strong> and <strong> localized gateway </strong> integrations, 
                enabling mobile money (M-Pesa, TigoPesa, AirtelMoney) and card processing. 
                Our solutions ensure PCI-DSS compliance and automated reconciliation.
              </p>
              
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}